import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Calendar, MapPin, Video, CreditCard, LogIn, UserPlus,
  ChevronRight, CheckCircle2, Clock, AlertTriangle, XCircle,
  Camera, Users, Mic, Building, ArrowLeft, LogOut, FileText
} from 'lucide-react';
import { BACKEND_URL } from '@/api';

const API = BACKEND_URL + '/api';

// ============== AUTH HELPERS ==============
const getToken = () => localStorage.getItem('bookingToken');
const authHeaders = () => ({ 'Authorization': `Bearer ${getToken()}`, 'Content-Type': 'application/json' });
const isLoggedIn = () => !!getToken();

const FLEET_INFO = {
  lite: { icon: Camera, color: 'text-blue-400', bg: 'bg-blue-500/10 border-blue-500/30' },
  full: { icon: Video, color: 'text-green-400', bg: 'bg-green-500/10 border-green-500/30' },
  premium: { icon: Video, color: 'text-purple-400', bg: 'bg-purple-500/10 border-purple-500/30' },
  '4angle': { icon: Users, color: 'text-amber-400', bg: 'bg-amber-500/10 border-amber-500/30' },
};

const STATUS_STYLES = {
  payment_pending: { label: 'Payment Pending', cls: 'bg-yellow-600', icon: Clock },
  pending: { label: 'Under Review', cls: 'bg-amber-600', icon: Clock },
  approved: { label: 'Confirmed', cls: 'bg-green-600', icon: CheckCircle2 },
  rejected: { label: 'Rejected', cls: 'bg-red-600', icon: XCircle },
};

// ============== AUTH FORMS ==============
const AuthSection = ({ onAuth }) => {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ email: '', password: '', name: '', organization: '', phone: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const endpoint = mode === 'login' ? '/booking/auth/login' : '/booking/auth/register';
      const body = mode === 'login' ? { email: form.email, password: form.password } : form;
      const res = await fetch(`${API}${endpoint}`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Authentication failed');
      localStorage.setItem('bookingToken', data.token);
      localStorage.setItem('bookingClient', JSON.stringify(data));
      onAuth(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-md mx-auto" data-testid="booking-auth-section">
      <Card className="bg-gray-800/60 border-gray-700">
        <CardHeader className="text-center">
          <CardTitle className="text-white text-xl">
            {mode === 'login' ? 'Client Login' : 'Create Client Account'}
          </CardTitle>
          <p className="text-gray-400 text-sm">Book game recordings with NL Sports</p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            {mode === 'register' && (
              <>
                <div>
                  <Label className="text-gray-300">Full Name</Label>
                  <Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })}
                    placeholder="Your name" className="bg-gray-700 border-gray-600 text-white mt-1" required data-testid="booking-name-input" />
                </div>
                <div>
                  <Label className="text-gray-300">Organization / Team</Label>
                  <Input value={form.organization} onChange={e => setForm({ ...form, organization: e.target.value })}
                    placeholder="Team or league name" className="bg-gray-700 border-gray-600 text-white mt-1" data-testid="booking-org-input" />
                </div>
                <div>
                  <Label className="text-gray-300">Phone</Label>
                  <Input value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })}
                    placeholder="(555) 555-5555" className="bg-gray-700 border-gray-600 text-white mt-1" data-testid="booking-phone-input" />
                </div>
              </>
            )}
            <div>
              <Label className="text-gray-300">Email</Label>
              <Input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })}
                placeholder="email@example.com" className="bg-gray-700 border-gray-600 text-white mt-1" required data-testid="booking-email-input" />
            </div>
            <div>
              <Label className="text-gray-300">Password</Label>
              <Input type="password" value={form.password} onChange={e => setForm({ ...form, password: e.target.value })}
                placeholder="Password" className="bg-gray-700 border-gray-600 text-white mt-1" required data-testid="booking-password-input" />
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full bg-sky-500 hover:bg-sky-600" data-testid="booking-auth-submit">
              {loading ? 'Please wait...' : mode === 'login' ? <><LogIn className="w-4 h-4 mr-2" /> Sign In</> : <><UserPlus className="w-4 h-4 mr-2" /> Create Account</>}
            </Button>
            <p className="text-center text-gray-400 text-sm">
              {mode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <button type="button" className="text-sky-400 hover:underline" onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); }}>
                {mode === 'login' ? 'Sign up' : 'Sign in'}
              </button>
            </p>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// ============== BOOKING FORM ==============
const BookingForm = ({ client, onBookingCreated }) => {
  const [venues, setVenues] = useState([]);
  const [fleetTypes, setFleetTypes] = useState([]);
  const [step, setStep] = useState(1);
  const [form, setForm] = useState({
    venue_id: '', custom_venue_name: '', custom_venue_address: '',
    sport: 'Soccer', fleet_type: 'full', game_date: '', game_time: '',
    has_elevated_area: true, add_commentator: false, commentator_name: '', notes: ''
  });
  const [useCustomVenue, setUseCustomVenue] = useState(false);
  const [quote, setQuote] = useState(null);
  const [quoteLoading, setQuoteLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [venueSearch, setVenueSearch] = useState('');
  const [locationLabels, setLocationLabels] = useState({});

  useEffect(() => {
    fetch(`${API}/booking/venues`).then(r => r.json()).then(setVenues).catch(() => {});
    fetch(`${API}/booking/pricing/fleet-types`).then(r => r.json()).then(d => setFleetTypes(d.fleet_types || [])).catch(() => {});
    fetch(`${API}/booking/pricing/fulfillment-locations`).then(r => r.json()).then(locs => {
      const labels = {};
      locs.forEach(l => { labels[l.slug] = l.name; });
      setLocationLabels(labels);
    }).catch(() => {});
  }, []);

  const filteredVenues = venues.filter(v =>
    v.name.toLowerCase().includes(venueSearch.toLowerCase())
  );

  const selectedVenue = venues.find(v => v.id === form.venue_id);

  const getQuote = useCallback(async () => {
    if (!form.game_date) return;
    setQuoteLoading(true);
    try {
      const res = await fetch(`${API}/booking/pricing/calculate`, {
        method: 'POST', headers: authHeaders(),
        body: JSON.stringify({
          venue_id: useCustomVenue ? null : form.venue_id,
          fleet_type: form.fleet_type,
          game_date: form.game_date,
          has_elevated_area: form.has_elevated_area,
          add_commentator: form.add_commentator,
          custom_venue_address: form.custom_venue_address,
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to calculate price');
      setQuote(data);
    } catch (err) { setError(err.message); }
    finally { setQuoteLoading(false); }
  }, [form, useCustomVenue]);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError('');
    try {
      const body = {
        ...form,
        venue_id: useCustomVenue ? null : form.venue_id,
        origin_url: window.location.origin,
      };
      const res = await fetch(`${API}/booking/bookings`, {
        method: 'POST', headers: authHeaders(), body: JSON.stringify(body)
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Booking failed');
      // Redirect to Stripe
      if (data.url) window.location.href = data.url;
    } catch (err) { setError(err.message); }
    finally { setSubmitting(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="booking-form">
      {/* Progress */}
      <div className="flex items-center gap-2 mb-6">
        {[1, 2, 3].map(s => (
          <React.Fragment key={s}>
            <button onClick={() => s < step && setStep(s)}
              className={`w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm transition-all ${
                step >= s ? 'bg-sky-500 text-white' : 'bg-gray-700 text-gray-400'
              }`}>{s}</button>
            {s < 3 && <div className={`flex-1 h-0.5 ${step > s ? 'bg-sky-500' : 'bg-gray-700'}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* STEP 1: Venue + Sport */}
      {step === 1 && (
        <Card className="bg-gray-800/60 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2"><MapPin className="w-5 h-5 text-sky-400" /> Venue & Sport</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-300">Sport</Label>
              <select value={form.sport} onChange={e => setForm({ ...form, sport: e.target.value })}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2 mt-1" data-testid="booking-sport-select">
                <option>Soccer</option><option>Gaelic Football</option><option>Hurling/Camogie</option><option>Rugby</option>
              </select>
            </div>

            <div className="flex gap-3">
              <Button variant={!useCustomVenue ? 'default' : 'outline'} size="sm"
                className={!useCustomVenue ? 'bg-sky-500' : 'border-gray-600 text-gray-400'}
                onClick={() => setUseCustomVenue(false)} data-testid="known-venue-btn">
                <Building className="w-4 h-4 mr-1" /> Known Venue
              </Button>
              <Button variant={useCustomVenue ? 'default' : 'outline'} size="sm"
                className={useCustomVenue ? 'bg-sky-500' : 'border-gray-600 text-gray-400'}
                onClick={() => setUseCustomVenue(true)} data-testid="custom-venue-btn">
                <MapPin className="w-4 h-4 mr-1" /> New Venue
              </Button>
            </div>

            {!useCustomVenue ? (
              <div>
                <Input placeholder="Search venues..." value={venueSearch}
                  onChange={e => setVenueSearch(e.target.value)}
                  className="bg-gray-700 border-gray-600 text-white mb-3" data-testid="venue-search" />
                <div className="max-h-64 overflow-y-auto space-y-1.5">
                  {filteredVenues.map(v => (
                    <button key={v.id} onClick={() => setForm({ ...form, venue_id: v.id })}
                      className={`w-full text-left p-3 rounded-lg border transition-all ${
                        form.venue_id === v.id
                          ? 'bg-sky-500/20 border-sky-500 text-white'
                          : 'bg-gray-700/50 border-gray-600 text-gray-300 hover:border-gray-500'
                      }`} data-testid={`venue-option-${v.id}`}>
                      <div className="font-medium text-sm">{v.name}</div>
                      <div className="text-xs text-gray-400 mt-0.5">
                        {Object.entries(v.pricing).map(([loc, price]) => (
                          <span key={loc} className="mr-3">{locationLabels[loc] || loc}: <span className="text-green-400">${price}</span></span>
                        ))}
                      </div>
                    </button>
                  ))}
                  {filteredVenues.length === 0 && <p className="text-gray-500 text-sm text-center py-4">No venues found. Try a different search or use "New Venue".</p>}
                </div>
              </div>
            ) : (
              <div className="space-y-3">
                <div>
                  <Label className="text-gray-300">Venue Name</Label>
                  <Input value={form.custom_venue_name} onChange={e => setForm({ ...form, custom_venue_name: e.target.value })}
                    placeholder="e.g., Riverside Soccer Complex" className="bg-gray-700 border-gray-600 text-white mt-1" data-testid="custom-venue-name" />
                </div>
                <div>
                  <Label className="text-gray-300">Venue Address</Label>
                  <Input value={form.custom_venue_address} onChange={e => setForm({ ...form, custom_venue_address: e.target.value })}
                    placeholder="Full street address" className="bg-gray-700 border-gray-600 text-white mt-1" data-testid="custom-venue-address" />
                </div>
              </div>
            )}

            <div>
              <Label className="text-gray-300">Does the venue have an elevated area for filming?</Label>
              <p className="text-xs text-gray-500 mb-2">Press box, observation deck, elevated seating, etc. A $35 surcharge applies if we need to set up a tall pole.</p>
              <div className="flex gap-3">
                <Button size="sm" variant={form.has_elevated_area ? 'default' : 'outline'}
                  className={form.has_elevated_area ? 'bg-green-600' : 'border-gray-600 text-gray-400'}
                  onClick={() => setForm({ ...form, has_elevated_area: true })} data-testid="elevated-yes">Yes</Button>
                <Button size="sm" variant={!form.has_elevated_area ? 'default' : 'outline'}
                  className={!form.has_elevated_area ? 'bg-amber-600' : 'border-gray-600 text-gray-400'}
                  onClick={() => setForm({ ...form, has_elevated_area: false })} data-testid="elevated-no">No (+$35)</Button>
              </div>
            </div>

            <Button onClick={() => setStep(2)} disabled={!useCustomVenue && !form.venue_id}
              className="w-full bg-sky-500 hover:bg-sky-600 mt-4" data-testid="step1-next">
              Next: Fleet & Date <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </CardContent>
        </Card>
      )}

      {/* STEP 2: Fleet + Date + Add-ons */}
      {step === 2 && (
        <Card className="bg-gray-800/60 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2"><Video className="w-5 h-5 text-sky-400" /> Fleet & Schedule</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label className="text-gray-300 mb-2 block">Fleet Type</Label>
              <div className="grid grid-cols-2 gap-3">
                {fleetTypes.map(ft => {
                  const info = FLEET_INFO[ft.id] || FLEET_INFO.full;
                  const Icon = info.icon;
                  return (
                    <button key={ft.id} onClick={() => setForm({ ...form, fleet_type: ft.id })}
                      className={`p-4 rounded-lg border text-left transition-all ${
                        form.fleet_type === ft.id
                          ? `${info.bg} border-current ${info.color}`
                          : 'bg-gray-700/50 border-gray-600 text-gray-300 hover:border-gray-500'
                      }`} data-testid={`fleet-option-${ft.id}`}>
                      <Icon className={`w-6 h-6 mb-1 ${form.fleet_type === ft.id ? info.color : 'text-gray-500'}`} />
                      <div className="font-semibold text-sm">{ft.name}</div>
                      <div className="text-xs text-gray-400">{ft.description}</div>
                      {ft.multiplier > 1 && <Badge className="mt-1 bg-amber-600 text-xs">{ft.multiplier}x base price</Badge>}
                    </button>
                  );
                })}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-gray-300">Game Date</Label>
                <Input type="date" value={form.game_date} onChange={e => setForm({ ...form, game_date: e.target.value })}
                  min={new Date().toISOString().split('T')[0]}
                  className="bg-gray-700 border-gray-600 text-white mt-1" data-testid="game-date-input" />
              </div>
              <div>
                <Label className="text-gray-300">Game Time</Label>
                <Input type="time" value={form.game_time} onChange={e => setForm({ ...form, game_time: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white mt-1" data-testid="game-time-input" />
              </div>
            </div>

            <div className="space-y-3 pt-2">
              <Label className="text-gray-300 block">Add-ons</Label>
              <label className="flex items-center gap-3 p-3 bg-gray-700/50 rounded-lg border border-gray-600 cursor-pointer hover:border-gray-500">
                <input type="checkbox" checked={form.add_commentator}
                  onChange={e => setForm({ ...form, add_commentator: e.target.checked })}
                  className="w-4 h-4 rounded" data-testid="commentator-checkbox" />
                <Mic className="w-5 h-5 text-purple-400" />
                <div>
                  <div className="text-white text-sm font-medium">Add Commentator (+$75)</div>
                  <div className="text-gray-400 text-xs">Live play-by-play commentary</div>
                </div>
              </label>
              {form.add_commentator && (
                <Input placeholder="Preferred commentator (optional)" value={form.commentator_name}
                  onChange={e => setForm({ ...form, commentator_name: e.target.value })}
                  className="bg-gray-700 border-gray-600 text-white" data-testid="commentator-name" />
              )}
            </div>

            <div>
              <Label className="text-gray-300">Notes</Label>
              <textarea value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })}
                placeholder="Any special requests or details..." rows={2}
                className="w-full bg-gray-700 border border-gray-600 text-white rounded-md p-2 mt-1 text-sm" data-testid="booking-notes" />
            </div>

            <div className="flex gap-3">
              <Button variant="outline" className="border-gray-600 text-gray-400" onClick={() => setStep(1)}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button onClick={() => { getQuote(); setStep(3); }} disabled={!form.game_date}
                className="flex-1 bg-sky-500 hover:bg-sky-600" data-testid="step2-next">
                Get Quote <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* STEP 3: Quote + Pay */}
      {step === 3 && (
        <Card className="bg-gray-800/60 border-gray-700">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2"><CreditCard className="w-5 h-5 text-sky-400" /> Review & Pay</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary */}
            <div className="bg-gray-900/50 rounded-lg p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-400">Venue</span><span className="text-white">{selectedVenue?.name || form.custom_venue_name || 'Custom Venue'}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Sport</span><span className="text-white">{form.sport}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Fleet</span><span className="text-white">{fleetTypes.find(f => f.id === form.fleet_type)?.name}</span></div>
              <div className="flex justify-between"><span className="text-gray-400">Date</span><span className="text-white">{form.game_date} {form.game_time}</span></div>
              {!form.has_elevated_area && <div className="flex justify-between"><span className="text-gray-400">Elevated Area</span><span className="text-amber-400">No — tall pole setup</span></div>}
              {form.add_commentator && <div className="flex justify-between"><span className="text-gray-400">Commentator</span><span className="text-purple-400">{form.commentator_name || 'Any available'}</span></div>}
            </div>

            {/* Quote breakdown */}
            {quoteLoading ? (
              <div className="text-center py-6 text-gray-400">Calculating quote...</div>
            ) : quote ? (
              <div className="bg-gray-900/80 rounded-lg p-4 space-y-2" data-testid="quote-breakdown">
                {quote.is_estimate && (
                  <div className="bg-amber-900/30 border border-amber-700/30 rounded-lg p-3 mb-3">
                    <p className="text-amber-300 text-sm flex items-center gap-2">
                      <AlertTriangle className="w-4 h-4" /> This is an estimated quote for a new venue. Final pricing may be adjusted after review.
                    </p>
                  </div>
                )}
                <div className="flex justify-between text-sm">
                  <span className="text-gray-400">Base Price</span>
                  <span className="text-white">${quote.base_price.toFixed(2)}</span>
                </div>
                {quote.multiplier > 1 && (
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">{quote.fleet_name} ({quote.multiplier}x)</span>
                    <span className="text-white">${quote.subtotal.toFixed(2)}</span>
                  </div>
                )}
                {quote.surcharges?.map((s, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="text-amber-400">{s.name}</span>
                    <span className="text-amber-400">+${s.amount.toFixed(2)}</span>
                  </div>
                ))}
                <div className="border-t border-gray-700 pt-2 mt-2 flex justify-between">
                  <span className="text-white font-bold">
                    {quote.is_estimate ? 'Estimated Total' : 'Total'}
                  </span>
                  <span className="text-2xl font-bold text-green-400">
                    {quote.is_estimate && 'At least '}${quote.total.toFixed(2)}
                  </span>
                </div>
              </div>
            ) : (
              <Button onClick={getQuote} className="w-full bg-amber-500 hover:bg-amber-600 text-black" data-testid="get-quote-btn">
                Calculate Quote
              </Button>
            )}

            {error && <p className="text-red-400 text-sm">{error}</p>}

            <p className="text-xs text-gray-500">
              Full payment is collected upfront. Your booking will be reviewed and you'll receive a confirmation email once approved. If rejected, a full refund will be issued.
            </p>

            <div className="flex gap-3">
              <Button variant="outline" className="border-gray-600 text-gray-400" onClick={() => setStep(2)}>
                <ArrowLeft className="w-4 h-4 mr-1" /> Back
              </Button>
              <Button onClick={handleSubmit} disabled={submitting || !quote}
                className="flex-1 bg-green-600 hover:bg-green-700 text-white" data-testid="pay-and-book-btn">
                {submitting ? 'Processing...' : <><CreditCard className="w-4 h-4 mr-2" /> Pay & Book — ${quote?.total?.toFixed(2) || '0'}</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// ============== CLIENT DASHBOARD ==============
const ClientDashboard = ({ client, onLogout }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  useEffect(() => {
    fetch(`${API}/booking/bookings`, { headers: authHeaders() })
      .then(r => r.json()).then(data => { setBookings(Array.isArray(data) ? data : []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Poll for payment completion
  useEffect(() => {
    const bookingId = searchParams.get('booking_id');
    const paymentStatus = searchParams.get('payment');
    if (bookingId && paymentStatus === 'success') {
      const poll = setInterval(async () => {
        try {
          const res = await fetch(`${API}/booking/bookings/${bookingId}/payment-status`, { headers: authHeaders() });
          const data = await res.json();
          if (data.status === 'pending' || data.payment_status === 'paid') {
            clearInterval(poll);
            // Refresh bookings
            const bRes = await fetch(`${API}/booking/bookings`, { headers: authHeaders() });
            const bData = await bRes.json();
            setBookings(Array.isArray(bData) ? bData : []);
          }
        } catch {}
      }, 2000);
      return () => clearInterval(poll);
    }
  }, [searchParams]);

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="client-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-white">My Bookings</h2>
          <p className="text-gray-400 text-sm">{client?.name} — {client?.organization || 'Individual'}</p>
        </div>
        <div className="flex gap-2">
          <Button onClick={() => navigate('/booking/new')} className="bg-sky-500 hover:bg-sky-600" data-testid="new-booking-btn">
            <Calendar className="w-4 h-4 mr-2" /> New Booking
          </Button>
          <Button variant="outline" className="border-gray-600 text-gray-400" onClick={onLogout} data-testid="booking-logout-btn">
            <LogOut className="w-4 h-4 mr-1" /> Logout
          </Button>
        </div>
      </div>

      {searchParams.get('payment') === 'success' && (
        <div className="bg-green-900/30 border border-green-700/30 rounded-lg p-4">
          <p className="text-green-300 flex items-center gap-2">
            <CheckCircle2 className="w-5 h-5" /> Payment received! Your booking is now under review. We'll email you once it's confirmed.
          </p>
        </div>
      )}

      {loading ? (
        <p className="text-gray-400 text-center py-8">Loading bookings...</p>
      ) : bookings.length === 0 ? (
        <Card className="bg-gray-800/60 border-gray-700">
          <CardContent className="text-center py-12">
            <Calendar className="w-12 h-12 text-gray-600 mx-auto mb-3" />
            <p className="text-gray-400">No bookings yet.</p>
            <Button onClick={() => navigate('/booking/new')} className="mt-4 bg-sky-500 hover:bg-sky-600">Book Your First Game</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {bookings.map(b => {
            const st = STATUS_STYLES[b.status] || STATUS_STYLES.pending;
            const StIcon = st.icon;
            return (
              <Card key={b.id} className="bg-gray-800/60 border-gray-700">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <Badge className={`${st.cls} text-white text-xs`}><StIcon className="w-3 h-3 mr-1" />{st.label}</Badge>
                        {b.is_estimate && <Badge className="bg-amber-600/50 text-amber-200 text-xs">Estimate</Badge>}
                      </div>
                      <h3 className="text-white font-semibold">{b.venue_name}</h3>
                      <div className="text-gray-400 text-sm mt-1 space-y-0.5">
                        <div className="flex items-center gap-4">
                          <span><Calendar className="w-3 h-3 inline mr-1" />{b.game_date} {b.game_time}</span>
                          <span><Video className="w-3 h-3 inline mr-1" />{b.fleet_name}</span>
                          <span>{b.sport}</span>
                        </div>
                        {b.surcharges?.length > 0 && (
                          <div className="text-xs text-amber-400">
                            Surcharges: {b.surcharges.map(s => s.name).join(', ')}
                          </div>
                        )}
                        {b.admin_notes && <p className="text-xs text-gray-500 mt-1">Admin: {b.admin_notes}</p>}
                      </div>
                    </div>
                    <div className="text-right ml-4">
                      <div className="text-xl font-bold text-green-400">${(b.adjusted_total || b.total).toFixed(2)}</div>
                      {b.adjusted_total && b.adjusted_total !== b.total && (
                        <div className="text-xs text-gray-500 line-through">${b.total.toFixed(2)}</div>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

// ============== MAIN BOOKING PAGE ==============
const BookingPage = () => {
  const [client, setClient] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Determine view from URL path
  const path = window.location.pathname;
  const isNewBooking = path.includes('/booking/new');
  const isDashboard = path.includes('/booking/dashboard') || (isLoggedIn() && !isNewBooking);

  useEffect(() => {
    const token = getToken();
    if (token) {
      fetch(`${API}/booking/auth/me`, { headers: authHeaders() })
        .then(r => { if (!r.ok) throw new Error(); return r.json(); })
        .then(data => { setClient(data); setLoading(false); })
        .catch(() => { localStorage.removeItem('bookingToken'); setLoading(false); });
    } else {
      setLoading(false);
    }
  }, []);

  const handleLogout = () => {
    localStorage.removeItem('bookingToken');
    localStorage.removeItem('bookingClient');
    setClient(null);
    navigate('/booking');
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-gray-400">Loading...</div>;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-gray-950 py-8 px-4" data-testid="booking-page">
      {/* Header */}
      <div className="max-w-4xl mx-auto mb-8 text-center">
        <h1 className="text-3xl sm:text-4xl font-bold text-white mb-2">Book a Game Recording</h1>
        <p className="text-gray-400">Professional sports production — single camera to full 4-angle setups</p>
      </div>

      {!client ? (
        <AuthSection onAuth={(data) => { setClient(data); navigate('/booking/dashboard'); }} />
      ) : isNewBooking ? (
        <BookingForm client={client} onBookingCreated={() => navigate('/booking/dashboard')} />
      ) : (
        <ClientDashboard client={client} onLogout={handleLogout} />
      )}
    </div>
  );
};

export default BookingPage;
